import { PayloadAction } from '@reduxjs/toolkit';

import { ICartEntry } from '@rbi-ctg/menu';
import { IOffersFragment, OfferRedemptionType } from 'generated/graphql-gateway';
import { createAppSlice } from 'state/global-state/utils';
import { IncentiveEvaluationMap } from 'state/loyalty/hooks/types';
import { LoyaltyAppliedOffer, LoyaltyOffer } from 'state/loyalty/types';

import { IEntriesIdsMap, IOffersState } from './offers.types';
import {
  getAppliedOffersFromStorage,
  getSelectedOfferFromStorage,
  removeSelectedOfferInStorage,
  updateAppliedOffersInStorage,
  updateSelectedOfferInStorage,
} from './offers.utils';

export const initialState: IOffersState = {
  appliedOffers: getAppliedOffersFromStorage(),
  cartEntriesIdsMap: {},
  cmsOffers: null,
  offers: [],
  offersFeedbackMap: {},
  offersLoading: false,
  personalizedOffers: [],
  selectedOffer: getSelectedOfferFromStorage(),
  surpriseAvailable: false,
  upsizeAvailable: false,
  userOffers: [],
};

export const offersSlice = createAppSlice({
  name: 'offers',
  initialState,
  reducers: {
    applyOffer: (state, { payload: newOffer }: PayloadAction<LoyaltyAppliedOffer>) => {
      // filter the non-stackable offers and ensuring that we are not adding the same offer
      const filterPredicate = newOffer.isStackable
        ? (appliedOffer: LoyaltyAppliedOffer) => appliedOffer.id !== newOffer.id
        : (appliedOffer: LoyaltyAppliedOffer) => appliedOffer.isStackable;

      const updatedOffers = state.appliedOffers.filter(filterPredicate).concat(newOffer);
      updateAppliedOffersInStorage(updatedOffers);
      state.appliedOffers = updatedOffers;
    },
    resetAppliedOffers: state => {
      updateAppliedOffersInStorage([]);
      state.appliedOffers = [];
    },
    resetOfferFeedbackMap: state => {
      state.offersFeedbackMap = {};
    },
    resetSurpriseAvailability: state => {
      state.surpriseAvailable = false;
    },
    resetUpsizeAvailability: state => {
      state.upsizeAvailable = false;
    },
    setCartEntriesIdsMap: (state, { payload }: PayloadAction<IEntriesIdsMap>) => {
      state.cartEntriesIdsMap = payload;
    },
    setCmsOffers: (state, { payload }: PayloadAction<LoyaltyOffer[]>) => {
      const offersMap = payload.reduce((acc, offer) => {
        if (offer?.loyaltyEngineId) {
          acc[offer.loyaltyEngineId] = offer;
        }
        return acc;
      }, {});
      const updatedOffers = state.appliedOffers.filter(offer => !!offersMap[offer.id || '']);
      updateAppliedOffersInStorage(updatedOffers);
      state.appliedOffers = updatedOffers;
      state.cmsOffers = payload;
    },
    setOffers: (state, { payload }: PayloadAction<IOffersFragment[]>) => {
      state.offers = payload;
    },
    setOffersFeedbackMap: (state, { payload }: PayloadAction<IncentiveEvaluationMap>) => {
      state.offersFeedbackMap = payload;
    },
    setOffersLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.offersLoading = payload;
    },
    setPersonalizedOffers: (state, { payload }: PayloadAction<LoyaltyOffer[]>) => {
      state.personalizedOffers = payload;
    },
    setSelectedOffer: (state, { payload }: PayloadAction<LoyaltyOffer | null>) => {
      updateSelectedOfferInStorage(payload);
      state.selectedOffer = payload;
    },
    setSurpriseOfferIfAvailable: (state, { payload: offer }: PayloadAction<IOffersFragment>) => {
      if (offer.redemptionType === OfferRedemptionType.SURPRISE) {
        state.surpriseAvailable = true;
      }
    },
    setUpsizeAvailable: (state, { payload }: PayloadAction<boolean>) => {
      state.upsizeAvailable = payload;
    },
    setUserOffers: (state, { payload }: PayloadAction<IOffersFragment[]>) => {
      state.userOffers = payload;
    },
    removeAppliedOfferByCartEntry: (state, { payload: cartEntry }: PayloadAction<ICartEntry>) => {
      const updatedOffers = state.appliedOffers.filter(offer => {
        if (
          cartEntry.cartId === offer.cartId &&
          offer.id === state.selectedOffer?.loyaltyEngineId
        ) {
          removeSelectedOfferInStorage();
        }
        // If cart entry has children, cartIds could match
        // otherwise, cartId should check against Swap cartId
        return offer?.swap && !cartEntry.children.length
          ? offer?.swap?.cartId !== cartEntry?.cartId
          : offer?.cartId !== cartEntry?.cartId;
      });

      updateAppliedOffersInStorage(updatedOffers);

      state.appliedOffers = updatedOffers;
    },
  },
});
